import * as Urls from '../_external/util/urls';

import * as Types from '../model/types';
import * as OpKinds from '../opKinds';
import * as Graph from '../graph';
import {hash} from '../hash';

const makeProjectOp = OpKinds.makeTaggingStandardOp;

const projectArgTypes = {
  project: 'project' as const,
};

const projectArgDescription = `A ${Graph.docType('project')}`;

export const opGetProjectTag = OpKinds.makeTagGetterOp({
  name: 'tag-project',
  tagName: 'project',
  tagType: 'project',
});

export const opProjectInternalId = makeProjectOp({
  hidden: true,
  name: 'project-internalId',
  argTypes: projectArgTypes,
  description: `Returns the internal id of the ${Graph.docType('project')}`,
  argDescriptions: {project: projectArgDescription},
  returnValueDescription: `The internal id of the ${Graph.docType('project')}`,
  returnType: inputTypes => 'string',
  resolver: ({project}) => {
    return project.id;
  },
});

export const opProjectCreatedAt = makeProjectOp({
  name: 'project-createdAt',
  argTypes: projectArgTypes,
  description: `Returns the creation time of the ${Graph.docType('project')}`,
  argDescriptions: {project: projectArgDescription},
  returnValueDescription: `The creation time of the ${Graph.docType(
    'project'
  )}`,
  returnType: inputTypes => 'date',
  resolver: ({project}) => new Date(project.createdAt + 'Z'),
});

export const opProjectName = makeProjectOp({
  name: 'project-name',
  argTypes: projectArgTypes,
  description: `Returns the name of the ${Graph.docType('project')}`,
  argDescriptions: {project: projectArgDescription},
  returnValueDescription: `The name of the ${Graph.docType('project')}`,
  returnType: inputTypes => 'string',
  resolver: ({project}) => project.name,
});

export const opProjectLink = makeProjectOp({
  hidden: true,
  name: 'project-link',
  argTypes: projectArgTypes,
  description: `Returns the link to the ${Graph.docType('project')}`,
  argDescriptions: {project: projectArgDescription},
  returnValueDescription: `The link to the ${Graph.docType('project')}`,
  returnType: inputTypes => 'link',
  resolver: ({project}) => ({
    name: project.name,
    url: Urls.project({
      name: project.name,
      entityName: project.entityName,
    }),
  }),
});

// Note this doesn't do the right thing at all if we try to request more than
// one run off a single project!
export const opProjectRun = makeProjectOp({
  hidden: true,
  name: 'project-run',
  argTypes: {...projectArgTypes, runName: 'string'},
  description: `Returns the ${Graph.docType(
    'run'
  )} with the given name from a ${Graph.docType('project')}`,
  argDescriptions: {
    project: projectArgDescription,
    runName: `The name of the ${Graph.docType('run')}`,
  },
  returnValueDescription: `The ${Graph.docType(
    'run'
  )} with the given name from a ${Graph.docType('project')}`,
  returnType: inputTypes => 'run',
  resolver: ({project, runName}) => {
    const alias = `run_${hash(runName)}`;
    return project[alias];
  },
});

export const opProjectRuns = makeProjectOp({
  name: 'project-runs',
  argTypes: projectArgTypes,
  description: `Returns the ${Graph.docType('run', {
    plural: true,
  })} from a ${Graph.docType('project')}`,
  argDescriptions: {project: projectArgDescription},
  returnValueDescription: `The ${Graph.docType('run', {
    plural: true,
  })} from a ${Graph.docType('project')}`,
  returnType: inputTypes => Types.list('run'),
  resolver: ({project}) => project.runs.edges.map((edge: any) => edge.node),
});

// This is a version of opProjectRuns that allows us to manually
// provide a filter parameter to pass to the GQL field.  This is
// a performance stopgap, not intended for end users.  Do not unhide.
export const opProjectFilteredRuns = makeProjectOp({
  hidden: true,
  name: 'project-filtered-runs',
  argTypes: {...projectArgTypes, filter: 'string', order: 'string'},
  description: `Returns the ${Graph.docType('run', {
    plural: true,
  })} from a ${Graph.docType('project')} with a filter applied`,
  argDescriptions: {
    project: projectArgDescription,
    filter: `The filter to apply to the ${Graph.docType('run', {
      plural: true,
    })}`,
    order: `The order to return the ${Graph.docType('run', {plural: true})}`,
  },
  returnValueDescription: `The ${Graph.docType('run', {
    plural: true,
  })} from a ${Graph.docType('project')} with a filter applied`,
  returnType: inputTypes => Types.list('run'),
  resolver: ({project, filter}) => {
    const alias = `filteredRuns_${hash(filter)}`;
    if (typeof project[alias] === 'undefined') {
      throw new Error(
        `opProjectedFilteredRuns couldn't find expected project field ${alias}`
      );
    }
    return project[alias].edges.map((edge: any) => edge.node);
  },
});

export const opProjectArtifactType = makeProjectOp({
  name: 'project-artifactType',
  argTypes: {
    ...projectArgTypes,
    artifactType: 'string',
  },
  description: `Returns the ${Graph.docType(
    'artifactType'
  )} for a given name within a ${Graph.docType('project')}`,
  argDescriptions: {
    project: projectArgDescription,
    artifactType: `The name of the ${Graph.docType('artifactType')}`,
  },
  returnValueDescription: `The ${Graph.docType(
    'artifactType'
  )} for a given name within a ${Graph.docType('project')}`,
  returnType: inputTypes => 'artifactType',
  resolver: ({project, artifactType}) => {
    const alias = `artifactType_${hash(artifactType)}`;
    return project[alias];
  },
});

export const opProjectArtifactTypes = makeProjectOp({
  name: 'project-artifactTypes',
  argTypes: {
    ...projectArgTypes,
  },
  description: `Returns the ${Graph.docType('artifactType', {
    plural: true,
  })} for a ${Graph.docType('project')}`,
  argDescriptions: {
    project: projectArgDescription,
  },
  returnValueDescription: `The ${Graph.docType('artifactType', {
    plural: true,
  })} for a ${Graph.docType('project')}`,
  returnType: inputTypes => Types.list('artifactType'),
  resolver: ({project}) =>
    project.artifactTypes.edges.map((artifactType: any) => artifactType.node),
});

export const opProjectArtifact = makeProjectOp({
  hidden: true,
  name: 'project-artifact',
  argTypes: {
    ...projectArgTypes,
    artifactName: 'string',
  },
  description: `Returns the ${Graph.docType(
    'artifact'
  )} for a given name within a ${Graph.docType('project')}`,
  argDescriptions: {
    project: projectArgDescription,
    artifactName: `The name of the ${Graph.docType('artifact')}`,
  },
  returnValueDescription: `The ${Graph.docType(
    'artifact'
  )} for a given name within a ${Graph.docType('project')}`,
  returnType: inputTypes => 'artifact',
  resolver: ({project, artifactName}) => {
    const alias = `artifactSequence_${hash(artifactName)}`;
    return project.artifactTypes.edges?.[0]?.node?.[alias] ?? null;
  },
});

export const opProjectArtifactVersion = makeProjectOp({
  // hidden: true,
  name: 'project-artifactVersion',
  argTypes: {
    ...projectArgTypes,
    artifactName: 'string',
    artifactVersionAlias: 'string',
  },
  description: `Returns the ${Graph.docType(
    'artifactVersion'
  )} for a given name and version within a ${Graph.docType('project')}`,
  argDescriptions: {
    project: projectArgDescription,
    artifactName: `The name of the ${Graph.docType('artifactVersion')}`,
    artifactVersionAlias: `The version alias of the ${Graph.docType(
      'artifactVersion'
    )}`,
  },
  returnValueDescription: `The ${Graph.docType(
    'artifactVersion'
  )} for a given name and version within a ${Graph.docType('project')}`,
  returnType: inputTypes => 'artifactVersion',
  resolver: ({project, artifactName, artifactVersionAlias}) => {
    const fullName = artifactName + ':' + artifactVersionAlias;
    const alias = `artifact_${hash(fullName)}`;
    return project[alias];
  },
});

export const opProjectReports = makeProjectOp({
  hidden: true,
  name: 'project-reports',
  argTypes: projectArgTypes,
  description: `Returns the ${Graph.docType('report', {
    plural: true,
  })} for a ${Graph.docType('project')}`,
  argDescriptions: {project: projectArgDescription},
  returnValueDescription: `The ${Graph.docType('report', {
    plural: true,
  })} for a ${Graph.docType('project')}`,
  returnType: inputTypes => Types.list('report'),
  resolver: ({project}) => project.allViews.edges.map((v: any) => v.node),
});

export const opProjectArtifacts = makeProjectOp({
  hidden: true,
  name: 'project-artifacts',
  argTypes: projectArgTypes,
  description: `Returns the ${Graph.docType('artifact', {
    plural: true,
  })} for a ${Graph.docType('project')}`,
  argDescriptions: {project: projectArgDescription},
  returnValueDescription: `The ${Graph.docType('artifact', {
    plural: true,
  })} for a ${Graph.docType('project')}`,
  returnType: inputTypes => Types.list('artifact'),
  resolver: ({project}) =>
    project.artifactTypes.edges.flatMap((at: any) =>
      at.node.artifactSequences.edges.map((artifact: any) => artifact.node)
    ),
});
